"use client";

import { Capacitor } from "@capacitor/core";
import {
  SafeArea,
  SystemBarsStyle,
  SystemBarsType
} from "@capacitor-community/safe-area";
import { useEffect, useId } from "react";

const DEFAULT_TOP = "#ffffff";
const DEFAULT_BOTTOM = "#ffffff";

type Entry = { id: string; top: string; bottom: string };
const stack: Entry[] = [];

function currentColors() {
  const last = stack[stack.length - 1];
  return last
    ? { top: last.top, bottom: last.bottom }
    : { top: DEFAULT_TOP, bottom: DEFAULT_BOTTOM };
}

// WCAG relative luminance from a #rrggbb (or #rgb) string. Returns 1 on parse
// failure so we fall back to "light" treatment, which is the safer default.
function relativeLuminance(hex: string): number {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const m = h.match(/.{2}/g);
  if (!m || m.length < 3) return 1;
  const [r, g, b] = m.map((pair) => {
    const v = parseInt(pair, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function styleFor(color: string) {
  return relativeLuminance(color) > 0.5
    ? SystemBarsStyle.Light // light background → dark icons
    : SystemBarsStyle.Dark;
}

function applyNativeBars(top: string, bottom: string) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    SafeArea.setSystemBarsStyle({
      style: styleFor(top),
      type: SystemBarsType.StatusBar
    });
    if (Capacitor.getPlatform() === "android") {
      SafeArea.setSystemBarsStyle({
        style: styleFor(bottom),
        type: SystemBarsType.NavigationBar
      });
    }
  } catch {
    // best-effort; never block the UI on a plugin issue
  }
}

function applyColors() {
  if (typeof document === "undefined") return;
  const { top, bottom } = currentColors();

  let meta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]'
  );
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  if (meta.content !== top) meta.content = top;

  const root = document.documentElement;
  root.style.setProperty("--edge-top", top);
  root.style.setProperty("--edge-bottom", bottom);

  applyNativeBars(top, bottom);
}

/**
 * Declares the edge colors of the current screen.
 *  · top    → Safari URL bar tint (meta name=theme-color) on mobile web.
 *           → status bar icon style (light/dark) on Capacitor iOS + Android.
 *  · bottom → --edge-bottom CSS var, painted by EdgeColorsBottomStrip behind
 *    the home-indicator safe area on mobile web.
 *           → navigation bar icon style on Capacitor Android.
 *
 * Multiple instances stack; the most recently mounted wins.
 */
export default function EdgeColors({
  top,
  bottom
}: {
  top: string;
  bottom: string;
}) {
  const id = useId();

  // Mount/unmount bepaalt de plek in de stack ("most recently mounted wins");
  // een prop-update verandert het bestaande entry IN PLACE. Voorheen was dit
  // één effect op [id, top, bottom]: elke prop-wissel (bijv. hideFooter in
  // AppLayout) deed remove+push en stal zo de voorrang van later gemounte
  // declaraties — precies wat de documentatie hieronder niet belooft.
  useEffect(() => {
    stack.push({ id, top, bottom });
    applyColors();
    return () => {
      const idx = stack.findIndex((e) => e.id === id);
      if (idx !== -1) stack.splice(idx, 1);
      applyColors();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const entry = stack.find((e) => e.id === id);
    if (entry && (entry.top !== top || entry.bottom !== bottom)) {
      entry.top = top;
      entry.bottom = bottom;
      applyColors();
    }
  }, [id, top, bottom]);

  return null;
}

/**
 * Paints --edge-bottom over the bottom safe-area inset (iOS home indicator
 * area in mobile Safari with viewport-fit=cover). Renders nothing visible on
 * devices with no inset. Mount once near the layout root.
 */
export function EdgeColorsBottomStrip() {
  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        height: "env(safe-area-inset-bottom)",
        background: "var(--edge-bottom, #ffffff)",
        pointerEvents: "none",
        // Sits above the page background but below modal scrims
        // (BottomSheet z-50, ExitConfirmSheet z-60) so overlays cover it.
        zIndex: 1
      }}
    />
  );
}
